import { useEffect, useRef, useState } from "react";
import {
  initializeDOMRefs,
  handleKeyboardEscape,
  processSearch,
  getText,
  renderCloneNode,
} from "../components/floating-chat/utils";
import { useAnimatedSearch } from "./use-animation-search";
import { Product } from "../types";

interface AnalyzedResult {
  sector: string;
  description: string;
  isNewContext: boolean;
  usePreviousChat: boolean;
  intent: string;
}

export const useSearchLogic = (
  searchValue: string,
  setSearchValue: (v: string) => void,
  backendUrl: string,
) => {
  const [searchActive, setSearchActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [productArea, setProductArea] = useState<HTMLElement | null>(null);
  const [pagination, setPagination] = useState<HTMLElement | null>(null);
  const [mainProductHeading, setMainProductHeading] =
    useState<HTMLElement | null>(null);
  const typingTextElementRef = useRef<HTMLElement | null>(null);
  const typingAnimationPromiseRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    initializeDOMRefs(setProductArea, setPagination, setMainProductHeading);
  }, []);

  useEffect(() => {
    return handleKeyboardEscape(() => setSearchActive(false));
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchValue.trim() && searchActive && !loading) {
      onSearch();
    }
  };

  const onSearch = () => {
    processSearch({
      searchValue,
      productArea,
      pagination,
      mainProductHeading,
      setMainProductHeading,
      setLoading,
      getDescription,
      typingTextElementRef,
    });
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const getDescription = async (productHeading: HTMLElement) => {
    try {
      const response = await fetch(`${backendUrl}/api/chat/analyze`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          brandId: window.helium.brandData.storeId,
        },
        body: JSON.stringify({
          query: searchValue,
          previousContext: "",
          previousMessages: [],
          brandContext: {
            orgId: window.helium.brandData.orgId,
            storeId: window.helium.brandData.storeId,
          },
        }),
      });
      if (!response.ok) {
        throw new Error(`Failed to analyze: ${response.status}`);
      }
      const data = await response.json();
      productHeading.textContent = `Getting "${data.description}" for you...`;

      typingAnimationPromiseRef.current = getText({
        query: data?.description,
        brandInfo: "",
        typingTextElementRef,
        backendUrl,
      });
      getHandlesFromChat(data, productHeading);
    } catch (error) {
      console.error("Error in getDescription:", error);
      setLoading(false);
      const typingTextElement = typingTextElementRef.current;
      if (typingTextElement) {
        typingTextElement.textContent =
          "Error getting description. Please try again.";
        typingTextElement.style.display = "block";
      }
      if (productArea) productArea.innerHTML = "";
      if (productHeading) productHeading.textContent = "";
      pagination?.style.setProperty("display", "block");
    }
  };

  const getHandlesFromChat = async (
    analyzedData: AnalyzedResult,
    productHeading: HTMLElement,
  ) => {
    if (!productArea) {
      console.error("Product area DOM element not found.");
      setLoading(false);
      pagination?.style.setProperty("display", "block");
      return;
    }

    let handleData: string[] = [];
    let searchError = false;

    try {
      const response = await fetch(`${backendUrl}/api/chat/search-products`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          brandId: window.helium.brandData.storeId,
        },
        body: JSON.stringify({
          query: analyzedData.description,
          sector: analyzedData.sector,
          intent: analyzedData?.intent,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        handleData = data?.data || [];
      } else {
        console.error("Error searching products:", response.statusText);
        searchError = true;
      }
    } catch (error) {
      console.error("Exception during getHandlesFromChat:", error);
      searchError = true;
    }

    fetchProducts(
      handleData,
      analyzedData?.description,
      productHeading,
      searchError,
      typingAnimationPromiseRef.current,
    );
  };

  const fetchProducts = async (
    handleData: string[],
    description: string,
    currentMainHeading: HTMLElement,
    searchError: boolean,
    typingPromise: Promise<void> | null,
  ) => {
    if (!productArea) {
      setLoading(false);
      pagination?.style.setProperty("display", "block");
      return;
    }

    let products: Product[] = [];
    if (handleData?.length && !searchError) {
      try {
        const productPromise = Promise.all(
          handleData
            .map(async (item) => {
              try {
                const details = await window.helium.datastore.getProduct({
                  id: item,
                });
                return details?.details || null;
              } catch (error) {
                console.error(
                  `Error fetching product details for ${item}:`,
                  error,
                );
                return null;
              }
            })
            .filter(Boolean),
        );
        products = (await productPromise).filter(Boolean);
      } catch (error) {
        console.error("Exception during product details fetch:", error);
        products = [];
        searchError = true;
      }
    }

    const productHtmls = await Promise.all(
      products.map((item) => renderCloneNode(item)),
    );

    const finalHtml = productHtmls
      .map((html) => html?.__html)
      .filter(Boolean)
      .join("");

    await typingPromise;

    const typingTextElement = typingTextElementRef.current;
    if (typingTextElement) {
      typingTextElement.style.display = "none";
    }

    productArea.innerHTML = "";

    if (!products.length || searchError) {
      productArea.innerHTML = `<h2 id="no-products-heading" class="hp-text-gray-500 hp-text-center hp-capitalize hp-mx-auto hp-mt-4" style="font-size: 14px; font-weight: medium;">${searchError ? "Error fetching product details." : `No valid products found for "${description}".`}</h2>`;
      if (currentMainHeading)
        currentMainHeading.textContent = searchError
          ? `Search failed`
          : `No products found`;
    } else {
      requestAnimationFrame(() => {
        if (productArea) {
          productArea.innerHTML = finalHtml;
          if (finalHtml) {
            productArea.scrollIntoView({ behavior: "smooth" });
          }
        }
      });

      if (currentMainHeading) {
        currentMainHeading.textContent = description
          ? `Curated ${description} for you`
          : "";
        currentMainHeading.style.setProperty("display", "block");
      }
    }

    setLoading(false);
    setSearchValue("");
    // pagination?.style.setProperty("display", "block");
  };
  return {
    searchActive,
    setSearchActive,
    loading,
    handleInputChange,
    handleKeyDown,
    onSearch,
    typingTextElementRef,
  };
};
